import { isNotNullOrUndefined } from 'util/typeGuards';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/store/rootReducer';
import {
  BudgetSpendByMonthAndPaymentCard,
  HighnotePaymentCard,
  HNAmountLimitSpendRule,
  HNCardFormFactor,
  HNPageInfo,
  HNPaymentCard,
  HNPaymentCardStatus,
  HNVelocitySpendRuleAmountBalance,
  PaidolUserToHighnotePaymentCard,
} from 'API';
import type { GetCardsPayload, GetCardsPayloadResult } from 'CDK_API';
import {
  activatePaymentCard,
  closePaymentCard,
  fetchAssinableCards,
  fetchAssignableCardsLite,
  fetchCardsByPaymentCardIds,
  fetchGroupCards,
  getCard,
  getCardDetails,
  getCardholderNames,
  getCardList,
  getHighnotePaymentCard,
  GetHighnotePaymentCardResults,
  getPaymentCardDetails,
  getPaymentCards,
  issuePaymentCardForFinancialAccount,
  loadMoreGroupCards,
  PaymentCardDetails,
  suspendPaymentCard,
  unlockPaymentCard,
  updateCardGroup,
  updateCardStatus,
  updateHighnotePaymentCard,
} from './cardsThunks';

export const MY_CARDS_PAGE_TOKEN_KEY = 'MY_CARDS_PAGE_TOKEN';

interface PaginationInfo {
  nextToken?: string;
  total?: number;
}

interface CardBalance {
  currentBalance: { value: number; currencyCode: string };
  remainingBalance: { value: number; currencyCode: string };
}

interface SpecificCardState {
  formFactor: HNCardFormFactor;
  status: HNPaymentCardStatus | null;
  balance: CardBalance;
  cardProduct: { name: string };
  velocityRule?: HNAmountLimitSpendRule;
  velocityRuleBalance?: HNVelocitySpendRuleAmountBalance;
  maximumTransactionAmount?: { value: number; currencyCode: string };
  highnotePaymentCardBudget?: BudgetSpendByMonthAndPaymentCard;
  paymentCardDetails?: PaymentCardDetails;
  cardUsers?: PaidolUserToHighnotePaymentCard[];
  updatePending: boolean;
  isCurrentCardActivated: boolean;
  isAdminLocked: boolean;
  lastActionByRole: string | null;
  lastActionByPaidolUserId: string | null;
}

export interface CardDataState {
  initialized: boolean;
  cardsLoading: boolean;
  panelOpen: boolean;
  hasFetchedUserCards: boolean;
  selectedCard?: HNPaymentCard;
  hnPageInfo?: HNPageInfo;
  pageInfo?: PaginationInfo;
  cardholderNames: Record<string, string[]>;
  userCardList: Array<GetCardsPayload> | [];
  paymentCardList: Array<GetCardsPayload> | [];
  paymentCards: Array<GetHighnotePaymentCardResults>;
  cardDetails: Map<string, HighnotePaymentCard>;
  specificCard: SpecificCardState;
  newCardIssued: boolean;
  openCardDetail?: GetCardsPayload;
  groupCards: GetCardsPayload[];
  groupCardsLoading: boolean;
  groupCardsPageInfo?: PaginationInfo;
  assinableCards: GetCardsPayload[];
  assinableCardsLoading: boolean;
  searchResults: GetCardsPayload[];
  searchResultsLoading: boolean;
  updateGroup: number;
  reloadGroupCards: number;
  error: string | null;
}

const initialSpecificCardState: SpecificCardState = {
  formFactor: HNCardFormFactor.VIRTUAL,
  status: null,
  balance: {
    currentBalance: { value: 0, currencyCode: 'USD' },
    remainingBalance: { value: 0, currencyCode: 'USD' },
  },
  cardProduct: { name: '' },
  updatePending: false,
  isCurrentCardActivated: false,
  isAdminLocked: false,
  lastActionByRole: null,
  lastActionByPaidolUserId: null,
  paymentCardDetails: undefined,
};

const initialState: CardDataState = {
  panelOpen: false,
  cardsLoading: false,
  hasFetchedUserCards: false,
  initialized: false,
  pageInfo: undefined,
  cardholderNames: {},
  paymentCardList: [],
  userCardList: [],
  paymentCards: [],
  cardDetails: new Map(), // cardListDetails
  specificCard: initialSpecificCardState,
  openCardDetail: undefined,
  newCardIssued: false,
  groupCards: [],
  groupCardsLoading: false,
  groupCardsPageInfo: undefined,
  assinableCards: [],
  assinableCardsLoading: false,
  searchResults: [],
  searchResultsLoading: false,
  updateGroup: 0,
  reloadGroupCards: 0,
  error: null,
};

const cardDataSlice = createSlice({
  name: 'cards/cardData',
  initialState,
  reducers: {
    resetCardIssueState(state) {
      state.newCardIssued = false;
    },
    /* Card list */
    resetCardSlice: (state) => {
      return initialState;
    },
    setPanelOpen(state, action) {
      state.panelOpen = action.payload;
    },
    setSelectedCard(state, action) {
      state.selectedCard = action.payload;
    },
    clearUserCardList: (state) => {
      state.userCardList = [];
    },
    /* Specific card */
    resetSpecificCard(state) {
      state.specificCard = initialSpecificCardState;
    },
    setIsCurrentCardActivated(state, action) {
      state.specificCard.isCurrentCardActivated = action.payload;
    },
    setOpenCardDetail(state, action) {
      state.openCardDetail = action.payload;
    },
    resetOpenCardDetail(state) {
      state.openCardDetail = undefined;
      state.specificCard.paymentCardDetails = undefined;
    },
    /* Group cards */
    resetGroupCards: (state) => {
      state.groupCards = [];
      state.groupCardsLoading = false;
      state.groupCardsPageInfo = undefined;
      state.error = null;
    },
    resetAssinableCards: (state) => {
      state.assinableCards = [];
      state.assinableCardsLoading = false;
      state.error = null;
    },
    resetSearchResults: (state) => {
      state.searchResults = [];
      state.searchResultsLoading = false;
      state.error = null;
    },
    triggerGroupUpdate: (state) => {
      state.updateGroup += 1;
    },
    triggerGroupCardsReload: (state) => {
      state.updateGroup += 1;
      state.reloadGroupCards = (state.reloadGroupCards || 0) + 1;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(issuePaymentCardForFinancialAccount.pending, (state) => {
        state.cardsLoading = true;
      })
      .addCase(issuePaymentCardForFinancialAccount.fulfilled, (state, action) => {
        state.cardsLoading = false;
        if (action.payload.result && action.payload.formType !== 'UPDATE_CARD') {
          state.newCardIssued = true;
        }
      })
      .addCase(issuePaymentCardForFinancialAccount.rejected, (state) => {
        state.cardsLoading = false;
        state.newCardIssued = false;
      });

    /* Card list */
    builder
      .addCase(getCardList.pending, (state) => {
        state.cardsLoading = true;
      })
      .addCase(getCardList.rejected, (state, action) => {
        state.cardsLoading = false;
        // Mark as fetched even on failure to prevent infinite loading state
        const userId = action.meta.arg.filter?.userId;
        if (userId) {
          state.hasFetchedUserCards = true;
        }
      })
      .addCase(getCardList.fulfilled, (state, action) => {
        const userId = action.meta.arg.filter?.userId;

        if (action.payload) {
          if (userId) {
            // "My cards" page — populates a separate list
            state.userCardList = action.payload.items || [];
            state.hasFetchedUserCards = true;
          } else {
            // Card management page — fetches the full paidol set in one call;
            // sorting and visible-row pagination happen client-side over this
            // list (SEE-904). pageInfo.total/nextToken still tracked so the UI
            // can warn if the AppSync 5MB ceiling truncated the response.
            state.paymentCardList = action.payload.items ?? [];
            state.pageInfo = {
              nextToken: action.payload.nextToken ?? undefined,
              total: action.payload.total ?? undefined,
            };
            state.initialized = true;
          }
        }
        state.cardsLoading = false;
      });

    builder.addCase(getPaymentCards.fulfilled, (state, action) => {
      if (action.payload) {
        const { edges, pageInfo } = action.payload;

        if (edges) {
          const mapped: GetHighnotePaymentCardResults[] = edges.map((edge) => ({
            hnGetPaymentCardDetails: edge.node!,
          }));
          state.paymentCards.push(...mapped);
        }

        if (pageInfo) {
          state.hnPageInfo = pageInfo;
        }
      }
    });

    builder.addCase(getCardholderNames.fulfilled, (state, action) => {
      action.payload?.forEach((card) => {
        const names =
          card.paidolUsers?.items
            .map((i) =>
              i?.paidolUser?.user
                ? `${i!.paidolUser!.user!.first_name} ${i!.paidolUser!.user!.last_name}`.toUpperCase()
                : (i?.paidolUser?.email ?? 'Unknown').toUpperCase()
            )
            .filter(Boolean) ?? [];
        if (names.length) state.cardholderNames[card.paymentCardId] = names;
      });
    });

    builder.addCase(getCardDetails.fulfilled, (state, action) => {
      if (action.payload) {
        const card = action.payload;
        state.cardDetails.set(card.paymentCardId, card);
      }
    });

    builder.addCase(getPaymentCardDetails.fulfilled, (state, action) => {
      if (action.payload) {
        const details = action.payload;
        const newState = state.paymentCards.map((paymentCard) => {
          if (paymentCard.hnGetPaymentCardDetails.id === details.paymentCardDetails?.id) {
            paymentCard.getBudgetSpendByMonthAndPaymentCard = details.highnotePaymentCardBudget;
            paymentCard.getHighnotePaymentCard = details.highnotePaymentCard;
            paymentCard.hnGetPaymentCardDetails = details.paymentCardDetails;
          }

          return paymentCard;
        });
        state.paymentCards = newState;
      }
    });

    /* Specific card */
    builder.addCase(getHighnotePaymentCard.fulfilled, (state, action) => {
      const paymentCard = action.payload.paymentCardDetails as HNPaymentCard | undefined;

      const budget = action.payload.highnotePaymentCardBudget as BudgetSpendByMonthAndPaymentCard | undefined;

      const velocityRule = paymentCard?.attachedVelocityRules?.edges?.at(0)?.node?.cumulativeRule as
        | HNAmountLimitSpendRule
        | undefined;

      const velocityBalance = paymentCard?.velocitySpendRuleBalances?.at(0) as
        | HNVelocitySpendRuleAmountBalance
        | undefined;

      // Extract maximum transaction amount from attachedSpendRules
      const amountLimitSpendRuleEdge = paymentCard?.attachedSpendRules?.edges?.find((edge) => {
        const node = edge?.node as HNAmountLimitSpendRule | undefined;
        return !!node?.maximumAmount;
      });
      const amountLimitSpendRule = amountLimitSpendRuleEdge?.node as HNAmountLimitSpendRule | undefined;

      const maximumTransactionAmount = amountLimitSpendRule?.maximumAmount
        ? {
            value: amountLimitSpendRule.maximumAmount.value ?? 0,
            currencyCode: amountLimitSpendRule.maximumAmount.currencyCode ?? 'USD',
          }
        : undefined;

      const budgetSpendByCurrentCycle = action.payload.budgetSpendByCurrentCycle as
        | { amount: number; transactionCount: number }
        | undefined;

      state.specificCard.formFactor = paymentCard?.formFactor ?? initialState.specificCard.formFactor;
      state.specificCard.status = paymentCard?.status ?? initialState.specificCard.status;
      state.specificCard.cardProduct = paymentCard?.cardProduct?.name
        ? { name: paymentCard.cardProduct.name }
        : initialState.specificCard.cardProduct;
      state.specificCard.velocityRule = velocityRule ?? undefined;
      state.specificCard.maximumTransactionAmount = maximumTransactionAmount;

      // Use BudgetSpendByCurrentCycle only if velocityRule exists and has a monthly limit
      // Otherwise, use BudgetSpendByMonthAndPaymentCard for full monthly total
      if (budgetSpendByCurrentCycle && velocityRule && velocityRule.maximumAmount) {
        // Card has active velocity rule with monthly limit - use BudgetSpendByCurrentCycle
        state.specificCard.balance = {
          currentBalance: {
            currencyCode: 'USD',
            value: budgetSpendByCurrentCycle.amount || 0,
          },
          remainingBalance: {
            currencyCode: 'USD',
            value: velocityRule.maximumAmount.value
              ? velocityRule.maximumAmount.value - (budgetSpendByCurrentCycle.amount || 0)
              : 0,
          },
        };
      } else if (velocityRule && velocityBalance && velocityRule.maximumAmount) {
        // Fallback to Highnote velocity balance (when BudgetSpendByCurrentCycle doesn't exist yet)
        const { currentBalance, remainingBalance } = velocityBalance;

        state.specificCard.balance = {
          currentBalance: {
            currencyCode: currentBalance?.currencyCode || 'USD',
            value: currentBalance?.value || 0,
          },
          remainingBalance: {
            currencyCode: remainingBalance?.currencyCode || 'USD',
            value: remainingBalance?.value || 0,
          },
        };
      } else if (budget) {
        // Card has no active velocity rule with monthly limit - use BudgetSpendByMonthAndPaymentCard for full monthly total
        state.specificCard.balance = {
          currentBalance: { currencyCode: 'USD', value: budget.amount || 0 },
          remainingBalance: {
            currencyCode: 'USD',
            value: velocityBalance?.remainingBalance?.value || 0,
          },
        };
      } else {
        state.specificCard.balance = initialState.specificCard.balance;
      }

      state.specificCard.paymentCardDetails = action.payload;

      state.specificCard.cardUsers = action.payload.highnotePaymentCard?.paidolUsers?.items
        .filter((i) => isNotNullOrUndefined(i))
        .map((i) => i!);
      state.specificCard.isAdminLocked =
        action.payload.highnotePaymentCard?.isAdminLocked ?? initialState.specificCard.isAdminLocked;
      state.specificCard.lastActionByRole =
        action.payload.highnotePaymentCard?.lastActionByRole ?? initialState.specificCard.lastActionByRole;
      state.specificCard.lastActionByPaidolUserId =
        action.payload.highnotePaymentCard?.lastActionByPaidolUserId ??
        initialState.specificCard.lastActionByPaidolUserId;

      // Patch in-memory list rows from the fresh DDB record. getCard.fulfilled
      // also patches these lists but reads from OpenSearch, which lags behind
      // DDB on edits — without this, an edited card's new name/status doesn't
      // appear in the table until OS catches up (SEE-904).
      const fresh = action.payload.highnotePaymentCard;
      if (fresh?.paymentCardId) {
        for (const list of [
          state.paymentCardList,
          state.userCardList,
          state.groupCards,
          state.assinableCards,
        ]) {
          const row = list.find((card) => card.paymentCardId === fresh.paymentCardId);
          if (!row) continue;
          if (fresh.name) row.name = fresh.name;
          if (fresh.status) row.status = fresh.status;
        }
      }
    });

    builder.addCase(activatePaymentCard.pending, (state) => {
      state.specificCard.updatePending = true;
    });

    builder.addCase(activatePaymentCard.fulfilled, (state, action) => {
      const updated = action.payload;
      if (!updated?.status) {
        state.specificCard.updatePending = false;
        return;
      }

      const cardId = updated.id;
      const newStatus = updated.status;

      state.specificCard.status = newStatus;
      state.specificCard.updatePending = false;

      /* Update all card lists */
      for (const list of [
        state.paymentCardList,
        state.userCardList,
        state.groupCards,
        state.assinableCards,
      ]) {
        for (const card of list) {
          if (card.paymentCardId === cardId) {
            card.status = newStatus; // Immer draft mutation
          }
        }
      }
    });

    builder.addCase(updateHighnotePaymentCard.fulfilled, (state, action) => {
      if (action.payload) {
        state.specificCard.cardUsers = action.payload.paidolUsers?.items
          .filter((i) => isNotNullOrUndefined(i))
          .map((i) => i!);
      }
    });

    builder.addCase(updateCardStatus.fulfilled, (state, action) => {
      const updated = action.payload;
      if (!updated || updated.status == null) return;

      const cardId = updated.paymentCardId;
      const newStatus = updated.status as HNPaymentCardStatus;

      state.specificCard.status = newStatus;
      state.specificCard.cardUsers = updated.paidolUsers?.items.filter(isNotNullOrUndefined).map((i) => i!);

      for (const list of [
        state.paymentCardList,
        state.userCardList,
        state.groupCards,
        state.assinableCards,
      ]) {
        for (const row of list) {
          if (row.paymentCardId === cardId) row.status = newStatus;
        }
      }

      const cached = state.cardDetails.get(cardId);
      if (cached) cached.status = newStatus;

      state.paymentCards = state.paymentCards.map((pc) =>
        pc.hnGetPaymentCardDetails.id === cardId
          ? {
              ...pc,
              hnGetPaymentCardDetails: {
                ...pc.hnGetPaymentCardDetails,
                status: newStatus,
              },
            }
          : pc
      );
    });

    builder.addCase(updateCardGroup.fulfilled, (state, action) => {
      const { paymentCardId, group } = action.payload;

      // Create a proper CardGroup object for the Redux state
      const cardGroup = group
        ? {
            __typename: 'CardGroup' as const,
            id: group.id,
            name: group.name,
            paidolId: '',
            authPaidolId: '',
            createdAt: '',
            updatedAt: '',
          }
        : null;

      const highnoteCard = state.specificCard.paymentCardDetails?.highnotePaymentCard;
      if (highnoteCard?.paymentCardId === paymentCardId) {
        if (highnoteCard.group) {
          highnoteCard.group.id = cardGroup?.id ?? highnoteCard.group.id;
          highnoteCard.group.name = cardGroup?.name ?? highnoteCard.group.name;
        } else {
          highnoteCard.group = cardGroup ?? null;
        }
      }

      if (state.openCardDetail?.paymentCardId === paymentCardId) {
        state.openCardDetail.cardGroup = group
          ? {
              id: group.id,
              name: group.name,
            }
          : null;
      }

      const prev = state.groupCards.find((c) => c.paymentCardId === paymentCardId);
      const prevGroupId = prev?.cardGroup?.id ?? null;
      const nextGroupId = group?.id ?? null;

      const shouldRemoveFromCurrentList = prevGroupId != null && prevGroupId !== nextGroupId; // card left this group

      for (const list of [
        state.paymentCardList,
        state.userCardList,
        state.groupCards,
        state.assinableCards,
      ]) {
        for (const row of list) {
          if (row.paymentCardId === paymentCardId) {
            row.cardGroup = group
              ? {
                  id: group.id,
                  name: group.name,
                }
              : null;
          }
        }
      }

      if (shouldRemoveFromCurrentList) {
        const lengthBefore = state.groupCards.length;
        state.groupCards = state.groupCards.filter((c) => c.paymentCardId !== paymentCardId);
        if (
          lengthBefore > state.groupCards.length &&
          state.groupCardsPageInfo?.total &&
          state.groupCardsPageInfo.total > 0
        ) {
          state.groupCardsPageInfo.total -= 1;

          // Clear nextToken if we now have all the cards
          if (state.groupCardsPageInfo.total <= state.groupCards.length) {
            state.groupCardsPageInfo.nextToken = undefined;
          }
        }
      }

      const cached = state.cardDetails.get(paymentCardId);
      if (cached) {
        cached.group = cardGroup ?? null;
      }
    });

    builder.addCase(closePaymentCard.fulfilled, (state, action) => {
      const closedCard = action.payload;
      if (!closedCard?.status) return;

      const cardId = closedCard.id;
      const newStatus = closedCard.status;

      state.specificCard.status = newStatus;

      for (const list of [
        state.paymentCardList,
        state.userCardList,
        state.groupCards,
        state.assinableCards,
      ]) {
        for (const row of list) {
          if (row.paymentCardId === cardId) {
            row.status = newStatus;
          }
        }
      }

      const cached = state.cardDetails.get(cardId);
      if (cached) cached.status = newStatus;

      state.paymentCards = state.paymentCards.map((pc) =>
        pc.hnGetPaymentCardDetails.id === cardId
          ? {
              ...pc,
              hnGetPaymentCardDetails: {
                ...pc.hnGetPaymentCardDetails,
                status: newStatus,
              },
            }
          : pc
      );

      if (state.specificCard.paymentCardDetails?.paymentCardDetails?.id === cardId) {
        state.specificCard.paymentCardDetails!.paymentCardDetails.status = newStatus;
      }

      state.specificCard.updatePending = false;
    });

    builder.addCase(suspendPaymentCard.pending, (state) => {
      state.specificCard.updatePending = true;
    });

    builder.addCase(suspendPaymentCard.fulfilled, (state, action) => {
      const updated = action.payload;
      state.specificCard.updatePending = false;
      if (!updated?.status) return;

      const cardId = updated.id;
      const newStatus = updated.status;

      state.specificCard.status = newStatus;

      for (const list of [
        state.paymentCardList,
        state.userCardList,
        state.groupCards,
        state.assinableCards,
      ]) {
        for (const card of list) {
          if (card.paymentCardId === cardId) {
            card.status = newStatus;
          }
        }
      }

      const cached = state.cardDetails.get(cardId);
      if (cached) cached.status = newStatus;

      state.paymentCards = state.paymentCards.map((pc) =>
        pc.hnGetPaymentCardDetails.id === cardId
          ? {
              ...pc,
              hnGetPaymentCardDetails: {
                ...pc.hnGetPaymentCardDetails,
                status: newStatus,
              },
            }
          : pc
      );
    });

    builder.addCase(suspendPaymentCard.rejected, (state) => {
      state.specificCard.updatePending = false;
    });

    builder.addCase(unlockPaymentCard.pending, (state) => {
      state.specificCard.updatePending = true;
    });

    builder.addCase(unlockPaymentCard.fulfilled, (state, action) => {
      const updated = action.payload;
      state.specificCard.updatePending = false;
      if (!updated?.status) return;

      const cardId = updated.id;
      const newStatus = updated.status;

      state.specificCard.status = newStatus;

      for (const list of [
        state.paymentCardList,
        state.userCardList,
        state.groupCards,
        state.assinableCards,
      ]) {
        for (const card of list) {
          if (card.paymentCardId === cardId) {
            card.status = newStatus;
          }
        }
      }

      const cached = state.cardDetails.get(cardId);
      if (cached) cached.status = newStatus;

      state.paymentCards = state.paymentCards.map((pc) =>
        pc.hnGetPaymentCardDetails.id === cardId
          ? {
              ...pc,
              hnGetPaymentCardDetails: {
                ...pc.hnGetPaymentCardDetails,
                status: newStatus,
              },
            }
          : pc
      );
    });

    builder.addCase(unlockPaymentCard.rejected, (state) => {
      state.specificCard.updatePending = false;
    });

    builder
      .addCase(getCard.fulfilled, (state, action) => {
        const updatedCard = action.payload;
        if (!updatedCard) return;

        state.openCardDetail = updatedCard;

        for (const list of [
          state.paymentCardList,
          state.userCardList,
          state.groupCards,
          state.assinableCards,
        ]) {
          const cardIndex = list.findIndex((card) => card.paymentCardId === updatedCard.paymentCardId);
          if (cardIndex !== -1) {
            const existingCard = list[cardIndex];
            if (updatedCard.cardGroup) {
              existingCard.cardGroup = updatedCard.cardGroup;
            }
            if (updatedCard.name) {
              existingCard.name = updatedCard.name;
            }
            if (updatedCard.status) {
              existingCard.status = updatedCard.status;
            }
            if (updatedCard.cardholder) {
              existingCard.cardholder = updatedCard.cardholder;
            }
          }
        }
      })
      .addCase(getCard.rejected, (state) => {
        state.openCardDetail = undefined;
      });

    // Group cards
    builder.addCase(fetchGroupCards.pending, (state) => {
      state.groupCardsLoading = true;
      state.error = null;
    });
    builder.addCase(fetchGroupCards.fulfilled, (state, action) => {
      state.groupCardsLoading = false;
      state.groupCards = action.payload?.items || [];
      state.groupCardsPageInfo = {
        nextToken: action.payload?.nextToken ?? undefined,
        total: action.payload?.total ?? undefined,
      };
    });
    builder.addCase(fetchGroupCards.rejected, (state, action) => {
      state.groupCardsLoading = false;
      state.error = action.error.message || 'Failed to fetch group cards';
    });

    // Load more group cards
    builder.addCase(loadMoreGroupCards.pending, (state) => {
      state.groupCardsLoading = true;
      state.error = null;
    });
    builder.addCase(loadMoreGroupCards.fulfilled, (state, action) => {
      state.groupCardsLoading = false;
      state.groupCards = [...state.groupCards, ...(action.payload?.items || [])];
      state.groupCardsPageInfo = {
        nextToken: action.payload?.nextToken ?? undefined,
        total: action.payload?.total ?? undefined,
      };
    });
    builder.addCase(loadMoreGroupCards.rejected, (state, action) => {
      state.groupCardsLoading = false;
      state.error = action.error.message || 'Failed to load more group cards';
    });

    builder.addCase(fetchAssinableCards.pending, (state) => {
      state.assinableCardsLoading = true;
      state.searchResultsLoading = true;
      state.error = null;
    });
    builder.addCase(fetchAssinableCards.fulfilled, (state, action) => {
      state.assinableCardsLoading = false;
      state.searchResultsLoading = false;
      // If this is a search (has searchTerm), update searchResults
      // Otherwise, update assinableCards (we fetch all cards with limit: 1000, no pagination)
      const isSearch = action.meta.arg.searchTerm && action.meta.arg.searchTerm.trim() !== '';

      if (isSearch) {
        state.searchResults = action.payload?.items || [];
      } else {
        state.assinableCards = action.payload?.items || [];
        state.searchResults = []; // Clear search results when loading all cards
      }
    });
    builder.addCase(fetchAssinableCards.rejected, (state, action) => {
      state.assinableCardsLoading = false;
      state.searchResultsLoading = false;
      state.error = action.error.message || 'Failed to fetch assignable cards';
    });

    // Handle fetchAssignableCardsLite (same state fields as fetchAssinableCards)
    builder.addCase(fetchAssignableCardsLite.pending, (state) => {
      state.assinableCardsLoading = true;
      state.searchResultsLoading = true;
      state.error = null;
    });
    builder.addCase(fetchAssignableCardsLite.fulfilled, (state, action) => {
      state.assinableCardsLoading = false;
      state.searchResultsLoading = false;
      const isSearch = action.meta.arg.searchTerm && action.meta.arg.searchTerm.trim() !== '';

      if (isSearch) {
        state.searchResults = action.payload?.items || [];
      } else {
        state.assinableCards = action.payload?.items || [];
        state.searchResults = [];
      }
    });
    builder.addCase(fetchAssignableCardsLite.rejected, (state, action) => {
      state.assinableCardsLoading = false;
      state.searchResultsLoading = false;
      state.error = action.error.message || 'Failed to fetch assignable cards';
    });

    // Handle fetchCardsByPaymentCardIds
    builder.addCase(fetchCardsByPaymentCardIds.pending, (state) => {
      state.groupCardsLoading = true;
      state.error = null;
    });
    builder.addCase(
      fetchCardsByPaymentCardIds.fulfilled,
      (state, action: PayloadAction<GetCardsPayloadResult>) => {
        state.groupCardsLoading = false;
        state.groupCards = action.payload?.items || [];
      }
    );
    builder.addCase(fetchCardsByPaymentCardIds.rejected, (state, action) => {
      state.groupCardsLoading = false;
      state.error = action.error?.message ?? 'Failed to load cards';
    });
  },
});

export const {
  resetCardIssueState,
  resetCardSlice,
  setPanelOpen,
  setSelectedCard,
  resetSpecificCard,
  setIsCurrentCardActivated,
  setOpenCardDetail,
  resetOpenCardDetail,
  resetGroupCards,
  resetAssinableCards,
  resetSearchResults,
  triggerGroupUpdate,
  triggerGroupCardsReload,
  clearUserCardList,
} = cardDataSlice.actions;

export const selectCardsDataSlice = (state: RootState): CardDataState =>
  state.cards?.cardData ?? initialState;

export const selectSpecificCardSlice = (state: RootState): SpecificCardState =>
  state.cards?.cardData?.specificCard ?? initialSpecificCardState;

// Group cards selector
export interface GroupCardsState {
  groupCards: GetCardsPayload[];
  groupCardsLoading: boolean;
  groupCardsPageInfo?: PaginationInfo;
  assinableCards: GetCardsPayload[];
  assinableCardsLoading: boolean;
  searchResults: GetCardsPayload[];
  searchResultsLoading: boolean;
  error: string | null;
  updateGroup: number;
  reloadGroupCards: number;
}

export const groupCardsSelector = (state: RootState): GroupCardsState => ({
  groupCards: state.cards?.cardData?.groupCards || [],
  groupCardsLoading: state.cards?.cardData?.groupCardsLoading || false,
  groupCardsPageInfo: state.cards?.cardData?.groupCardsPageInfo,
  assinableCards: state.cards?.cardData?.assinableCards || [],
  assinableCardsLoading: state.cards?.cardData?.assinableCardsLoading || false,
  searchResults: state.cards?.cardData?.searchResults || [],
  searchResultsLoading: state.cards?.cardData?.searchResultsLoading || false,
  error: state.cards?.cardData?.error || null,
  updateGroup: state.cards?.cardData?.updateGroup || 0,
  reloadGroupCards: state.cards?.cardData?.reloadGroupCards || 0,
});

export default cardDataSlice.reducer;
